import { useMemo } from 'react';
import { useUIConfig } from '../contexts/UIConfigContext';
import { SALES_STAGES } from '../config/salesPlanning.config';
import { PURCHASE_STAGES } from '../config/purchasePlanning.config';
import { RECONCILIATION_STAGES } from '../config/reconciliation.config';
import { isStageViewAvailable, type StageConfig } from '../config/types';
import { usePlanningYear } from './usePlanningYear';

/**
 * Converts a stage path like 'stage-1' to a section prefix like 'sales_stage1'.
 * For reconciliation, the section is always 'reconciliation'.
 */
function buildSection(modulePrefix: string, stagePath: string): string {
  if (modulePrefix === 'reconciliation') return 'reconciliation';
  // 'stage-1' → 'stage1', 'stage-2' → 'stage2', etc.
  const normalized = stagePath.replace('-', '');
  return `${modulePrefix}_${normalized}`;
}

function useOverriddenStages(
  stages: StageConfig[],
  modulePrefix: string,
): StageConfig[] {
  const { getStageTitle, getStageDescription, getViewLabels } = useUIConfig();
  const [planningYear] = usePlanningYear();

  return useMemo(
    () =>
      stages.map((stage) => {
        const section = buildSection(modulePrefix, stage.path);
        const title = getStageTitle(section, stage.title);
        const description = getStageDescription(section, stage.description);

        let views = stage.views;
        if (views && views.length > 0) {
          const defaultLabels = views.map((v) => v.label);
          const overriddenLabels = getViewLabels(section, defaultLabels);
          // Лейблы из конфига сопоставляются по индексу — фильтруем по году уже после
          views = views
            .map((v, i) => ({
              ...v,
              label: overriddenLabels[i] ?? v.label,
            }))
            .filter((v) => isStageViewAvailable(v, planningYear));
        }

        return { ...stage, title, description, views };
      }),
    [stages, modulePrefix, getStageTitle, getStageDescription, getViewLabels, planningYear],
  );
}

export function useSalesStages(): StageConfig[] {
  return useOverriddenStages(SALES_STAGES, 'sales');
}

export function usePurchaseStages(): StageConfig[] {
  return useOverriddenStages(PURCHASE_STAGES, 'purchase');
}

export function useReconciliationStages(): StageConfig[] {
  return useOverriddenStages(RECONCILIATION_STAGES, 'reconciliation');
}
